import { GAME_LIST, type GameId } from "@playpal/core";

/**
 * What this browser remembers about the last time you played.
 *
 * Local only, and deliberately so: the two things below would otherwise need
 * an account, and the whole point of the guest path is that a child can play
 * without one. Nothing here is sent anywhere, and clearing site data clears it.
 */

const LAST_ROOM_KEY = "playpal.lastRoom";
const PLAYED_KEY = "playpal.played";
const PARTNERS_KEY = "playpal.partners";

/**
 * Enough for "the friends I actually play", not enough to become a contact
 * list. Five covers a best friend, a sibling and a couple of cousins; a longer
 * list would need scrolling, managing, and a conversation about deleting
 * people, none of which this app wants to have.
 */
const MAX_PARTNERS = 5;

/**
 * A room shuts down after two hours idle, so an older code is a dead end.
 * Slightly under, so the offer disappears before the room does rather than
 * after — a shortcut that fails is worse than no shortcut.
 */
const ROOM_LIKELY_ALIVE_MS = 105 * 60 * 1000;

interface LastRoom {
	code: string;
	at: number;
	/** Who you were playing, for the label. Just a made-up name and an emoji. */
	friendName: string | null;
	friendAvatar: string | null;
}

function read<T>(key: string): T | null {
	try {
		const raw = localStorage.getItem(key);
		return raw ? (JSON.parse(raw) as T) : null;
	} catch {
		// Blocked or full storage just means no shortcut this time.
		return null;
	}
}

function write(key: string, value: unknown): void {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// Same again: this is a convenience, never a requirement.
	}
}

export function rememberRoom(
	code: string,
	friend: { name: string; avatar: string } | null,
	now = Date.now(),
): void {
	write(LAST_ROOM_KEY, {
		code,
		at: now,
		friendName: friend?.name ?? null,
		friendAvatar: friend?.avatar ?? null,
	} satisfies LastRoom);
}

export function forgetRoom(): void {
	try {
		localStorage.removeItem(LAST_ROOM_KEY);
	} catch {
		// Nothing to do about it, and nothing depends on it.
	}
}

/** The room you were last in, if it could plausibly still be there. */
export function lastRoom(now = Date.now()): LastRoom | null {
	const saved = read<LastRoom>(LAST_ROOM_KEY);
	if (!saved?.code || typeof saved.at !== "number") return null;
	return now - saved.at < ROOM_LIKELY_ALIVE_MS ? saved : null;
}

/**
 * Someone you have actually played, so the front page can offer to set up a
 * rematch. Just a made-up name, an emoji, and — for a friend with a player
 * card — their public code. Nothing here can reach the friend: there is no
 * push channel, and "play again" only opens a fresh room whose code the child
 * still shares the way they always do.
 */
export interface Partner {
	name: string;
	avatar: string;
	/** Their profile code when they were signed in; null for a guest. */
	playerCode: string | null;
	/** When you last played them. */
	at: number;
}

/**
 * The same person, as well as this device can tell. When both have a player
 * card the code decides — the names come from short lists, so two different
 * friends really can be Cheerful Badger 🦊. Otherwise the made-up name plus
 * the animal is the best there is, and it also catches a guest who has since
 * signed in, so they do not appear twice.
 */
function samePartner(a: Partner, b: Partner): boolean {
	if (a.playerCode !== null && b.playerCode !== null) return a.playerCode === b.playerCode;
	return a.name === b.name && a.avatar === b.avatar;
}

export function rememberPartner(
	partner: { name: string; avatar: string; playerCode: string | null },
	now = Date.now(),
): void {
	const fresh: Partner = { ...partner, at: now };
	// Newest first, previous sightings of the same person dropped — playing
	// again moves a friend back to the top rather than adding them twice.
	const next = [fresh, ...partners().filter((kept) => !samePartner(fresh, kept))].slice(
		0,
		MAX_PARTNERS,
	);
	write(PARTNERS_KEY, next);
}

/** Friends played on this device, most recent first. */
export function partners(): Partner[] {
	const saved = read<Partner[]>(PARTNERS_KEY);
	if (!Array.isArray(saved)) return [];
	return saved.filter(
		(entry) =>
			typeof entry?.name === "string" &&
			typeof entry.avatar === "string" &&
			typeof entry.at === "number" &&
			(entry.playerCode === null || typeof entry.playerCode === "string"),
	);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How long ago, in words a child reads at a glance. Elapsed time rather than
 * calendar days: "yesterday" meaning "about a day ago" is the right kind of
 * true here, and nobody is auditing it.
 */
export function lastPlayedLabel(at: number, now = Date.now()): string {
	const days = Math.floor((now - at) / DAY_MS);
	if (days < 1) return "today";
	if (days === 1) return "yesterday";
	if (days <= 30) return `${days} days ago`;
	return "ages ago";
}

export function rememberGamePlayed(gameId: GameId): void {
	const played = new Set(playedGames());
	if (played.has(gameId)) return;
	played.add(gameId);
	write(PLAYED_KEY, [...played]);
}

export function playedGames(): GameId[] {
	const saved = read<GameId[]>(PLAYED_KEY);
	if (!Array.isArray(saved)) return [];
	// Filter against the catalogue, so a game that has since been removed does
	// not inflate the count above the number that exist.
	const known = new Set(GAME_LIST.map((game) => game.id));
	return saved.filter((id) => known.has(id));
}

/**
 * A game you have not played yet, or any game once you have played them all.
 * Forty-three is more than a child will ever scroll through, and the ones at
 * the bottom of the list were simply never being found.
 */
export function surpriseGame(pick = Math.random): GameId {
	const played = new Set(playedGames());
	const fresh = GAME_LIST.filter((game) => !played.has(game.id));
	const pool = fresh.length > 0 ? fresh : GAME_LIST;
	return pool[Math.floor(pick() * pool.length)].id;
}
